import * as Sentry from '@sentry/react';

import { APP_ERROR_CODES } from './error-codes';
import type { AppErrorCode } from './error-codes';

export { APP_ERROR_CODES };
export type { AppErrorCode };

const dsn = process.env.REACT_APP_SENTRY_DSN?.trim();

function isReactErrorBoundaryEvent(event: Sentry.ErrorEvent): boolean {
  const fromMechanism = event.exception?.values?.some(
    (v) => v.mechanism?.type === 'auto.function.react.error_boundary',
  );
  const reactCtx = event.contexts?.react as { componentStack?: unknown } | undefined;
  const fromContext =
    typeof reactCtx?.componentStack === 'string' && reactCtx.componentStack.length > 0;
  return Boolean(fromMechanism || fromContext);
}

function attachDefaultErrorCodeIfMissing(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.tags?.error_code) return event;
  if (!event.exception?.values?.length) return event;

  const code: AppErrorCode = isReactErrorBoundaryEvent(event) ? 'FE_0026' : 'FE_0025';
  event.tags = {
    ...event.tags,
    error_code: code,
    error_code_desc: APP_ERROR_CODES[code],
  };
  return event;
}

if (dsn) {
  const fromEnv = parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '');
  const tracesSampleRate = Number.isFinite(fromEnv)
    ? fromEnv
    : process.env.NODE_ENV === 'production'
      ? 0.1
      : 0;

  // browserTracingIntegration installs PerformanceObservers that buffer entries
  // (long tasks, paint, layout, resource). Skip it when sampling is 0 — otherwise
  // the buffer grows unbounded without ever being flushed to Sentry.
  const integrations = tracesSampleRate > 0 ? [Sentry.browserTracingIntegration()] : [];

  Sentry.init({
    dsn,
    sendDefaultPii: false,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
    ...(process.env.REACT_APP_SENTRY_RELEASE
      ? { release: process.env.REACT_APP_SENTRY_RELEASE }
      : {}),
    integrations,
    tracesSampleRate,
    beforeSend(event) {
      if (event.type === 'transaction') return event;
      return attachDefaultErrorCodeIfMissing(event as Sentry.ErrorEvent);
    },
  });
}

/** Normalize values passed to `trackError` / `captureException`. */
export function toTrackedError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export type TrackErrorOptions = {
  /** Key from `APP_ERROR_CODES` → Sentry `error_code`; description → `error_code_desc` */
  code: AppErrorCode;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  /** Optional grouping key for Sentry issues */
  fingerprint?: string[];
};

/**
 * Reports an error to Sentry when DSN is configured; otherwise logs in development.
 * @returns Sentry event id, or undefined if not sent
 */
export function trackError(error: unknown, options: TrackErrorOptions): string | undefined {
  const client = Sentry.getClient();
  const captureContext: Parameters<typeof Sentry.captureException>[1] = {};
  captureContext.tags = {
    error_code: options.code,
    error_code_desc: APP_ERROR_CODES[options.code],
    ...options.tags,
  };
  if (options.extra) captureContext.extra = options.extra;
  if (options.fingerprint?.length) captureContext.fingerprint = options.fingerprint;

  if (!client) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        '[trackError]',
        options.code,
        APP_ERROR_CODES[options.code],
        error,
        options.extra ?? options.tags ?? '',
      );
    }
    return undefined;
  }

  return Sentry.captureException(error, captureContext);
}

export { Sentry };
